import { get, set, del } from 'idb-keyval'
import { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

const CACHE_KEY = 'react-query-offline-cache'

/**
 * When true, persistClient() becomes a no-op. Used during bulk prefetch
 * to prevent PersistQueryClientProvider from dehydrating + serializing the
 * entire query cache on every setQueryData call (hundreds of calls in a
 * tight loop). Without this, WKWebView OOMs because the dehydrate→serialize
 * →structured-clone pipeline creates 3-4 copies of the full cache in memory.
 *
 * Call resumePersist() after prefetch to trigger a single final persist.
 */
let persistPaused = false
let pendingClient: PersistedClient | null = null

export function pausePersist(): void {
  persistPaused = true
  pendingClient = null
}

export async function resumePersist(): Promise<void> {
  persistPaused = false
  if (pendingClient) {
    const client = pendingClient
    pendingClient = null
    try {
      await set(CACHE_KEY, client)
    } catch (error) {
      console.error('[QueryPersister] Failed to persist deferred cache:', error)
    }
  }
}

/**
 * IndexedDB persister for React Query cache.
 * Enables offline-first functionality by persisting query cache across app restarts.
 */
export const createIDBPersister = (): Persister => {
  return {
    persistClient: async (client: PersistedClient) => {
      if (persistPaused) {
        // Capture the latest dehydrated snapshot so resumePersist() can flush it
        pendingClient = client
        return
      }
      try {
        await set(CACHE_KEY, client)
      } catch (error) {
        console.error('[QueryPersister] Failed to persist cache:', error)
      }
    },
    restoreClient: async () => {
      try {
        return await get<PersistedClient>(CACHE_KEY)
      } catch (error) {
        console.error('[QueryPersister] Failed to restore cache:', error)
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await del(CACHE_KEY)
      } catch (error) {
        console.error('[QueryPersister] Failed to remove cache:', error)
      }
    },
  }
}

export const idbPersister = createIDBPersister()
